import moment from 'moment';
import momentTz from 'moment-timezone';
import { downloadCsv, escapeCsvField } from './csv';

const ROUTE_TZ = 'Europe/Zurich';

export const ROUTE_CSV_FIELD_MAP = [
  ['id', 'Route ID'],
  ['date', 'Date'],
  ['driverName', 'Driver'],
  ['startTime', 'Start Time'],
  ['endTime', 'End Time'],
  // OVO-130 — shift duration (bike-out → bike-in) + client service time
  // (first pickup → last delivery), cleanly separated.
  ['duration', 'Shift Duration (HH:MM)'],
  ['clientService', 'Client Service (HH:MM)'],
  ['stops', '# of stops'],
  ['ordersCount', '# of orders'],
  ['partners', 'Partners'],
  ['distanceKm', 'Distance (km)'],
  // OVO-130 — optimizer-estimate durations (travel / working / service /
  // working %) dropped; Shift Duration + Client Service above are the real
  // billable/payroll numbers.
  ['co2Kg', 'CO2 (kg)'],
  ['status', 'Status'],
  ['startAddress', 'Start Address'],
  ['endAddress', 'End Address'],
  ['updatedAt', 'Updated At'],
];

// #134 — monthly HR timesheet columns (admin exports only: Routes page,
// route page). Appended so the columns above keep their positions. Never on a
// partner-scoped export: the shift and the pauses are the DRIVER's payroll
// data (OVO-002 / OVO-130). "Shift (min)" is the same number as Shift
// Duration (pauses already subtracted), in minutes so Airtable can sum it.
export const ROUTE_CSV_HR_FIELD_MAP = [
  ['driverId', 'Driver ID'],
  ['plannedStart', 'Planned Start'],
  ['plannedEnd', 'Planned End'],
  ['badgedStart', 'Badged Start'],
  ['badgedEnd', 'Badged End'],
  ['pausedHHMM', 'Paused (HH:MM)'],
  ['pauseCount', '# of pauses'],
  ['shiftMinutes', 'Shift (min)'],
  ['pausedMinutes', 'Paused (min)'],
  ['shiftBasis', 'Shift Basis'],
  ['autoClosed', 'Closed Automatically'],
  ['endCorrected', 'End Corrected'],
];

// #134 — "Export pauses": one row per pause (a route can have several), for
// the pause monitoring required by public regulation (Felipe, Oct 27 2026).
// The routes CSV keeps its per-route Paused totals. Admin-only like the HR
// columns: pauses are the DRIVER's data, never on a partner export.
export const ROUTE_PAUSES_CSV_FIELD_MAP = [
  ['routeId', 'Route ID'],
  ['date', 'Date'],
  ['driverId', 'Driver ID'],
  ['driverName', 'Driver'],
  ['pauseStart', 'Pause Start'],
  ['pauseEnd', 'Pause End'],
  ['minutes', 'Duration (min)'],
  ['endedBy', 'Ended By'],
  ['source', 'Source'],
  ['corrected', 'Corrected'],
  ['note', 'Note'],
];

// Re-exported for existing importers; the implementation is shared (csv.js).
export { escapeCsvField };

function formatDurationHHMM(startIso, endIso) {
  if (!startIso || !endIso) return '';
  const diff = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!diff || diff <= 0) return '';
  const totalMinutes = Math.floor(diff / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

// OVO-130 — minutes → "HH:MM" for the backend-computed durations.
function formatMinsHHMM(mins) {
  if (mins == null || mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function buildRouteCsvRow(route) {
  // Partner-scoped rows (partner portal / admin partner-routes tab): the backend
  // attaches the partner's own slice. Export ONLY this partner's numbers — their
  // first-pickup→last-delivery window, stop/order counts and client-service — not
  // the driver's whole-route shift, and other partners are never named
  // (OVO-002 — just "Shared").
  // OVO-236 — Shift Duration used to be hardcoded blank here, which is why the
  // Routes CSV came out with an empty column. The shift is driver-level, so the
  // backend sends `durations` ONLY to admins (never to a partner viewer); fill
  // it when present, leave it blank otherwise.
  const partnerScoped = route.partnerStartTime !== undefined;
  if (partnerScoped) {
    return {
      id: route.id,
      date: route.date ? moment(route.date).format('YYYY-MM-DD') : '',
      driverName: route?.driverName || route?.driver?.name || '',
      startTime: route.partnerStartTime || '',
      endTime: route.partnerEndTime || '',
      duration: formatMinsHHMM(route.durations?.shiftMins),
      clientService: formatMinsHHMM(route.partnerClientServiceMins),
      stops: route.partnerStops ?? 0,
      ordersCount: route.partnerOrderCount ?? 0,
      partners: route.mutualized ? 'Shared' : '',
      distanceKm: route.distance != null ? (route.distance / 1000).toFixed(2) : '',
      co2Kg: route.co2?.kg ?? '',
      status: route.status || '',
      startAddress: '',
      endAddress: '',
      updatedAt: route.updatedAt ? moment(route.updatedAt).format('YYYY-MM-DD HH:mm') : '',
    };
  }
  return {
    id: route.id,
    date: route.date ? moment(route.date).format('YYYY-MM-DD') : '',
    driverName: route?.driver?.name || '',
    startTime: route.actualShiftStart ? moment(route.actualShiftStart).format('HH:mm') : '',
    endTime: route.actualShiftEnd ? moment(route.actualShiftEnd).format('HH:mm') : '',
    duration:
      route.durations?.shiftMins != null
        ? formatMinsHHMM(route.durations.shiftMins)
        : formatDurationHHMM(route.actualShiftStart, route.actualShiftEnd),
    clientService: formatMinsHHMM(route.durations?.clientServiceMins),
    stops: route?._count?.steps ?? route?.steps?.length ?? 0,
    ordersCount: route?._count?.orders ?? route?.orders?.length ?? 0,
    partners: Array.isArray(route?.partners) ? route.partners.map((p) => p.name).join('; ') : '',
    distanceKm: route.distance != null ? (route.distance / 1000).toFixed(2) : '',
    co2Kg: route.co2?.kg ?? '',
    status: route.status || '',
    startAddress: route.startAddress || '',
    endAddress: route.endAddress || '',
    updatedAt: route.updatedAt ? moment(route.updatedAt).format('YYYY-MM-DD HH:mm') : '',
    ...buildRouteHrFields(route),
  };
}

const formatBadge = (iso) => {
  if (!iso) return '';
  const m = momentTz(iso);
  return m.isValid() ? m.tz(ROUTE_TZ).format('YYYY-MM-DD HH:mm') : '';
};

// #134 — the HR columns of one route (admin rows only).
// Shift Basis tells HR where Shift (min) comes from: "badged" = the driver's
// start + end badges (pauses subtracted), "planned" = no complete badge pair,
// so the planned window was used — those rows need a look before payroll.
export function buildRouteHrFields(route) {
  const d = route.durations || {};
  const badged = Boolean(route.actualShiftStart && route.actualShiftEnd);
  const planned = Boolean(route.shiftStart && route.shiftEnd);
  const shiftMins = d.shiftMins;
  const pausedMins = d.pausedMins ?? 0;
  return {
    driverId: route.driverId ?? route.driver?.id ?? '',
    plannedStart: route.shiftStart || '',
    plannedEnd: route.shiftEnd || '',
    badgedStart: formatBadge(route.actualShiftStart),
    badgedEnd: formatBadge(route.actualShiftEnd),
    pausedHHMM: pausedMins > 0 ? formatMinsHHMM(pausedMins) : '00:00',
    pauseCount: d.pauseCount ?? 0,
    shiftMinutes: shiftMins != null ? Math.round(shiftMins) : '',
    pausedMinutes: pausedMins,
    shiftBasis: shiftMins == null ? '' : badged ? 'badged' : planned ? 'planned' : '',
    autoClosed: route.autoClosedAt ? 'yes' : '',
    endCorrected: route.shiftCorrectedAt ? 'yes' : '',
  };
}

// #134 — the pause rows of one route (`route.pauses` from GET /routes).
// A pause never resumed ends with the shift-end badge, the same clip as the
// Paused (min) total ("Ended By" = route end); while the route is still
// paused, the end and the duration stay blank. Times are Zurich wall clock.
export function buildRoutePauseRows(route) {
  const shiftEnd = route.actualShiftEnd || null;
  return (route.pauses || []).map((p) => {
    const end = p.end || shiftEnd;
    const minutes = end ? Math.max(0, Math.round((new Date(end).getTime() - new Date(p.start).getTime()) / 60000)) : '';
    return {
      routeId: route.id,
      date: route.date ? moment(route.date).format('YYYY-MM-DD') : '',
      driverId: route.driverId ?? route.driver?.id ?? '',
      driverName: route.driver?.name || '',
      pauseStart: formatBadge(p.start),
      pauseEnd: formatBadge(end),
      minutes,
      endedBy: p.end ? 'resume' : shiftEnd ? 'route end' : '',
      source: p.simulatedByOperator ? 'operator' : 'driver',
      corrected: p.edited ? 'yes' : '',
      note: p.note || '',
    };
  });
}

// Returns the number of pause rows written (0 → nothing downloaded).
export function downloadRoutePausesCsv(routes) {
  const rows = routes.flatMap(buildRoutePauseRows);
  if (!rows.length) return 0;
  const headers = ROUTE_PAUSES_CSV_FIELD_MAP.map(([, header]) => header);
  downloadCsv(
    [
      headers.join(','),
      ...rows.map((row) => ROUTE_PAUSES_CSV_FIELD_MAP.map(([field]) => escapeCsvField(row[field])).join(',')),
    ],
    `pauses-export-${moment().format('YYYY-MM-DD')}.csv`,
  );
  return rows.length;
}

// `hr: true` (admin exports) appends the #134 timesheet columns.
export function downloadRoutesCsv(routes, { hr = false } = {}) {
  const fieldMap = hr ? [...ROUTE_CSV_FIELD_MAP, ...ROUTE_CSV_HR_FIELD_MAP] : ROUTE_CSV_FIELD_MAP;
  const headers = fieldMap.map(([, header]) => header);
  const rows = routes.map((route) => {
    const data = buildRouteCsvRow(route);
    return fieldMap.map(([field]) => escapeCsvField(data[field]));
  });
  downloadCsv(
    [headers.join(','), ...rows.map((r) => r.join(','))],
    `routes-export-${moment().format('YYYY-MM-DD')}.csv`,
  );
}
